import styled from 'styled-components'
import { ProductQuantityQuickAdd } from '@/features/shop/components/ProductQuantityQuickAdd'
import { type Product } from '@/features/shop/services/Product/types'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { type UpdateProductQuantityFn } from '@/features/shared/hooks/useUpdateProductQuantity'
import {
  type OnQtyCallback,
  type OnProductCallback,
  type OnProductClickCallback,
  type RenderQuickAdd,
} from '@/features/shop/components/ProductCard/types'
import { LandscapeProduct } from '@/features/shop/components/ProductCard/LandscapeProduct'
import { useProductCardQuickAdd } from '@/features/shop/components/ProductCard/useProductCardQuickAdd'
import { ProductLink } from '@/features/shop/components/ProductCard/ProductLink'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { screenSizes } from '@shipt/design-system-themes'
import { isGatedRoute } from '@shared/constants/accessLevel'
import { useCurrentRouteData } from '@/features/shared/hooks/routes/useCurrentRouteData'

type Props = {
  product: Product
  onQtyAdded: OnQtyCallback
  onQtyRemoved: OnProductCallback
  onClick: OnProductClickCallback
  onInView: OnProductCallback
  index: number
  productQuantity: number
  updateProductQuantity: UpdateProductQuantityFn
  hasBackground?: boolean
  renderQuickAdd?: RenderQuickAdd
  linkToUniversalPDP: boolean
  href?: string
}

export const LandscapeProductCard = ({
  product,
  onQtyAdded,
  onQtyRemoved,
  onClick,
  onInView,
  index = 0,
  productQuantity,
  updateProductQuantity,
  hasBackground,
  renderQuickAdd,
  linkToUniversalPDP,
  href,
}: Props) => {
  const routeData = useCurrentRouteData()

  const { handleAddQty, handleRemoveQty, isUpdatingQuantity } = useProductCardQuickAdd({
    index,
    product,
    onQtyAdded,
    onQtyRemoved,
    productQuantity,
    updateProductQuantity,
  })

  const quickAdd = renderQuickAdd
    ? renderQuickAdd({ index, product })
    : isGatedRoute(routeData?.access) && (
        <QuickAddWrapper hasBackground={hasBackground}>
          <ProductQuantityQuickAdd
            quantity={productQuantity}
            product={product}
            onAdd={handleAddQty}
            onRemove={handleRemoveQty}
            displayContext={false}
            showProductFlag={false}
            isUpdatingQuantity={isUpdatingQuantity}
          />
        </QuickAddWrapper>
      )

  return (
    <LandscapeProductScrollIntoView
      autoSize={hasBackground}
      onInView={() => onInView({ product, index })}
    >
      {quickAdd ? (
        <InternalWrapper>
          <SkeletonConsumer>{quickAdd}</SkeletonConsumer>
        </InternalWrapper>
      ) : null}
      <ProductCardLink
        product={product}
        index={index}
        onClick={onClick}
        linkToUniversalPDP={linkToUniversalPDP}
        href={href}
      >
        <LandscapeProduct product={product} hasBackground={hasBackground} />
      </ProductCardLink>
    </LandscapeProductScrollIntoView>
  )
}

const LandscapeProductScrollIntoView = styled(ScrollIntoView)`
  position: relative;
  padding-bottom: 0.5rem;
`

const InternalWrapper = styled.div`
  transition: opacity 0.2s ease-in-out;
  width: 6.5rem;
  height: 6.5rem;
  position: absolute;
  left: 0;
  top: 0;

  @media ${screenSizes.max_mobile} {
    width: 5.5rem;
    height: 5.5rem;
  }
`

const QuickAddWrapper = styled.div<{ hasBackground?: boolean }>`
  top: ${({ hasBackground }) => (hasBackground ? '47px' : '55px')};
  width: 110%;
  right: -10%;
  position: absolute;
  z-index: 3;
`

const ProductCardLink = styled(ProductLink)`
  width: 100%;
  display: flex;
  cursor: pointer;
  text-decoration: none;
`
